// Regional endemic-species checklists — the "how many of Hawaii's
// exclusive fish have you actually seen?" data set. Each region is a
// hand-curated list of species that occur only (or almost only) in
// that biogeographic zone. Users' lifelist entries are matched by
// normalised scientific name (shared/species.normName) so a species
// that came in from GBIF/OBIS with a slightly different casing still
// counts.
//
// Kept intentionally short — the ID convention "well-known endemic
// hobbyists in the region actually chase" beats a taxonomically-
// complete list that includes deep-water species no snorkeler will
// ever encounter. Grow the list as needed.

import { normName } from "./species";

export interface EndemicSpecies {
  scientific_name: string;
  common_name: string;
  // Optional one-line note surfaced next to the checkbox — "cleaner
  // wrasse mimic," "juveniles very different," etc. Helps hobbyists
  // recognise ones they might have logged under the wrong name.
  hint?: string;
}

export interface EndemicRegion {
  key: string;
  label: string;
  species: EndemicSpecies[];
}

// Regex over lifelist location_names and curated-beach regions. When a
// user has ANY logged entry / beach whose text matches, the region's
// card renders. A user who hasn't been to Hawaii doesn't see a
// Hawaii endemic scoreboard staring at 0/22 forever.
const REGION_MATCHERS: Record<string, RegExp> = {
  hawaii:
    /hawaii|hawaiʻi|maui|oʻahu|oahu|kauaʻi|kauai|big\s?island|kona|hilo|kohala|molokini|hanauma|napali|kealakekua|kealakekua|molokai|lanai/i,
  caribbean:
    /caribbean|bonaire|cozumel|belize|roat[aá]n|bahamas|cayman|cura[çc]ao|aruba|turks|tobago|jamaica|st\.?\s?(lucia|croix|john|thomas|kitts)|virgin islands|florida keys|key largo|riviera maya|puerto rico|dominican|antigua|barbados|grenada|martinique|guadeloupe/i,
  redsea:
    /red sea|\begypt\b|sharm|hurghada|dahab|marsa alam|el gouna|safaga|eilat|aqaba|\bsudan\b|jeddah/i,
  galapagos:
    /gal[aá]pagos|darwin island|wolf island|isabela island|santa cruz island|san crist[oó]bal|floreana|puerto ayora/i,
  mediterranean:
    /mediterranean|\bmalta\b|\bgozo\b|sardinia|sicily|corsica|balearic|ibiza|mallorca|menorca|dubrovnik|santorini|\bcrete\b|\bcyprus\b|amalfi|\bcapri\b|c[oô]te d.?azur|costa brava/i,
};

// Species list format: scientific name is the truth key. Common names
// are what users see. Hints stay short (< ~60 chars) so they fit next
// to the checkbox without wrapping to a third line on mobile.
export const ENDEMIC_REGIONS: EndemicRegion[] = [
  {
    key: "hawaii",
    label: "Hawaiian endemics",
    species: [
      {
        scientific_name: "Zebrasoma flavescens",
        common_name: "Yellow Tang",
        hint: "the iconic bright-yellow oval",
      },
      { scientific_name: "Chaetodon miliaris", common_name: "Milletseed Butterflyfish" },
      { scientific_name: "Chaetodon fremblii", common_name: "Bluestripe Butterflyfish" },
      { scientific_name: "Chaetodon multicinctus", common_name: "Multiband Butterflyfish" },
      { scientific_name: "Chaetodon tinkeri", common_name: "Tinker's Butterflyfish", hint: "deep — usually 25 m+" },
      { scientific_name: "Centropyge potteri", common_name: "Potter's Angelfish" },
      { scientific_name: "Apolemichthys arcuatus", common_name: "Bandit Angelfish" },
      { scientific_name: "Genicanthus personatus", common_name: "Masked Angelfish", hint: "rare and deep — a big-day sighting" },
      { scientific_name: "Dascyllus albisella", common_name: "Hawaiian Dascyllus" },
      { scientific_name: "Abudefduf abdominalis", common_name: "Hawaiian Sergeant" },
      { scientific_name: "Chromis verater", common_name: "Threespot Chromis" },
      { scientific_name: "Stegastes marginatus", common_name: "Hawaiian Gregory" },
      { scientific_name: "Thalassoma ballieui", common_name: "Blacktail Wrasse" },
      { scientific_name: "Thalassoma duperrey", common_name: "Saddle Wrasse" },
      { scientific_name: "Labroides phthirophagus", common_name: "Hawaiian Cleaner Wrasse" },
      { scientific_name: "Coris venusta", common_name: "Elegant Coris" },
      { scientific_name: "Coris flavovittata", common_name: "Yellowstripe Coris" },
      { scientific_name: "Bodianus albotaeniatus", common_name: "Hawaiian Hogfish" },
      { scientific_name: "Cirrhilabrus jordani", common_name: "Jordan's Fairy Wrasse", hint: "typically 30 m+" },
      { scientific_name: "Ctenochaetus hawaiiensis", common_name: "Chevron Tang", hint: "adults orange-brown; juveniles are the ones to look for" },
      { scientific_name: "Sargocentron xantherythrum", common_name: "Hawaiian Squirrelfish" },
      { scientific_name: "Priacanthus meeki", common_name: "Hawaiian Bigeye" },
      { scientific_name: "Canthigaster jactator", common_name: "Hawaiian Whitespotted Toby" },
    ],
  },
  {
    key: "caribbean",
    label: "Caribbean endemics",
    species: [
      { scientific_name: "Gramma loreto", common_name: "Royal Gramma", hint: "purple-and-gold, hangs upside-down under ledges" },
      { scientific_name: "Elacatinus oceanops", common_name: "Neon Goby", hint: "cleaner — tiny, electric-blue stripe" },
      { scientific_name: "Thalassoma bifasciatum", common_name: "Bluehead Wrasse", hint: "juveniles yellow; terminal males blue-headed" },
      { scientific_name: "Stegastes partitus", common_name: "Bicolor Damselfish" },
      { scientific_name: "Microspathodon chrysurus", common_name: "Yellowtail Damselfish" },
      { scientific_name: "Chaetodon capistratus", common_name: "Foureye Butterflyfish" },
      { scientific_name: "Holacanthus ciliaris", common_name: "Queen Angelfish" },
      { scientific_name: "Holacanthus tricolor", common_name: "Rock Beauty" },
      { scientific_name: "Pomacanthus paru", common_name: "French Angelfish" },
      { scientific_name: "Sparisoma viride", common_name: "Stoplight Parrotfish" },
      { scientific_name: "Scarus taeniopterus", common_name: "Princess Parrotfish" },
      { scientific_name: "Acanthurus coeruleus", common_name: "Blue Tang", hint: "Atlantic blue tang" },
      { scientific_name: "Epinephelus striatus", common_name: "Nassau Grouper", hint: "endangered — a real find" },
      { scientific_name: "Cephalopholis fulva", common_name: "Coney" },
      { scientific_name: "Lachnolaimus maximus", common_name: "Hogfish" },
      { scientific_name: "Aulostomus maculatus", common_name: "Atlantic Trumpetfish" },
      { scientific_name: "Halichoeres bivittatus", common_name: "Slippery Dick" },
    ],
  },
  {
    key: "redsea",
    label: "Red Sea endemics",
    species: [
      { scientific_name: "Amphiprion bicinctus", common_name: "Red Sea Anemonefish", hint: "the region's only clownfish" },
      { scientific_name: "Chaetodon semilarvatus", common_name: "Golden Butterflyfish", hint: "bright yellow, pairs under table coral" },
      { scientific_name: "Chaetodon austriacus", common_name: "Blacktail Butterflyfish" },
      { scientific_name: "Chaetodon paucifasciatus", common_name: "Eritrean Butterflyfish" },
      { scientific_name: "Chaetodon fasciatus", common_name: "Diagonal Butterflyfish" },
      { scientific_name: "Pomacanthus maculosus", common_name: "Yellowbar Angelfish" },
      { scientific_name: "Acanthurus sohal", common_name: "Sohal Surgeonfish", hint: "feisty — defends a turf" },
      { scientific_name: "Zebrasoma xanthurum", common_name: "Yellowtail Tang", hint: "deep purple with a yellow tail" },
      { scientific_name: "Larabicus quadrilineatus", common_name: "Fourline Wrasse", hint: "juveniles are cleaners" },
      { scientific_name: "Dascyllus marginatus", common_name: "Marginate Dascyllus" },
      { scientific_name: "Pseudanthias taeniatus", common_name: "Red Sea Anthias" },
      { scientific_name: "Pomacentrus sulfureus", common_name: "Sulphur Damsel" },
      { scientific_name: "Cheilinus abudjubbe", common_name: "Abudjubbe Wrasse" },
      { scientific_name: "Scarus ferrugineus", common_name: "Rusty Parrotfish" },
    ],
  },
  {
    key: "galapagos",
    label: "Galápagos specialties",
    species: [
      { scientific_name: "Mycteroperca olfax", common_name: "Sailfin Grouper", hint: "the bacalao — Galápagos endemic" },
      { scientific_name: "Stegastes beebei", common_name: "Galápagos Ringtail Damselfish", hint: "endemic" },
      { scientific_name: "Stegastes arcifrons", common_name: "Southern Whitetail Major", hint: "Galápagos & Cocos" },
      { scientific_name: "Azurina eupalama", common_name: "Blackspot Chromis", hint: "Galápagos endemic — rare" },
      { scientific_name: "Holacanthus passer", common_name: "King Angelfish" },
      { scientific_name: "Johnrandallia nigrirostris", common_name: "Barberfish", hint: "a cleaner — gathers at stations" },
      { scientific_name: "Prionurus laticlavius", common_name: "Razor Surgeonfish" },
      { scientific_name: "Bodianus diplotaenia", common_name: "Mexican Hogfish" },
      { scientific_name: "Thalassoma lucasanum", common_name: "Cortez Rainbow Wrasse" },
      { scientific_name: "Chaetodon humeralis", common_name: "Threebanded Butterflyfish" },
      { scientific_name: "Semicossyphus darwini", common_name: "Galápagos Sheephead", hint: "regional — eastern Pacific" },
    ],
  },
  {
    key: "mediterranean",
    label: "Mediterranean specialties",
    species: [
      { scientific_name: "Thalassoma pavo", common_name: "Ornate Wrasse" },
      { scientific_name: "Coris julis", common_name: "Mediterranean Rainbow Wrasse" },
      { scientific_name: "Chromis chromis", common_name: "Mediterranean Damselfish", hint: "clouds of them over rock" },
      { scientific_name: "Symphodus tinca", common_name: "Peacock Wrasse" },
      { scientific_name: "Serranus scriba", common_name: "Painted Comber" },
      { scientific_name: "Serranus cabrilla", common_name: "Comber" },
      { scientific_name: "Diplodus sargus", common_name: "White Seabream" },
      { scientific_name: "Diplodus vulgaris", common_name: "Two-banded Seabream" },
      { scientific_name: "Sarpa salpa", common_name: "Salema" },
      { scientific_name: "Epinephelus marginatus", common_name: "Dusky Grouper", hint: "big and shy — a highlight" },
      { scientific_name: "Mullus surmuletus", common_name: "Striped Red Mullet" },
      { scientific_name: "Apogon imberbis", common_name: "Mediterranean Cardinalfish" },
      { scientific_name: "Muraena helena", common_name: "Mediterranean Moray" },
      { scientific_name: "Sparus aurata", common_name: "Gilthead Seabream" },
    ],
  },
];

// Match a user's lifelist location_names + trip beach regions against
// region-detect patterns. Returns the set of regions worth showing.
export function detectRegions(
  locationNames: string[],
  beachRegions: string[] = [],
): string[] {
  const haystack = [...locationNames, ...beachRegions].join(" | ");
  const out: string[] = [];
  for (const [key, re] of Object.entries(REGION_MATCHERS)) {
    if (re.test(haystack)) out.push(key);
  }
  return out;
}

// The normalised scientific names of every endemic in the given regions —
// the one place the "is this species a regional endemic?" rule lives. Callers
// (Plan tagging, badge candidacy) build a lookup from this instead of
// re-walking ENDEMIC_REGIONS with their own normName loop, so the matching
// rule can never drift between surfaces. Pass no keys to include all regions.
export function endemicNameSet(regionKeys?: string[]): Set<string> {
  const wanted = regionKeys ? new Set(regionKeys) : null;
  const out = new Set<string>();
  for (const region of ENDEMIC_REGIONS) {
    if (wanted && !wanted.has(region.key)) continue;
    for (const s of region.species) {
      const k = normName(s.scientific_name);
      if (k) out.add(k);
    }
  }
  return out;
}

// Which endemic regions a species belongs to (by normalised scientific name).
// Same matching rule as endemicNameSet — used by badge candidacy so the
// "endemic:<region>" mapping never diverges from the scoreboard.
export function endemicRegionsForName(scientificName: string): string[] {
  const k = normName(scientificName);
  if (!k) return [];
  const out: string[] = [];
  for (const region of ENDEMIC_REGIONS) {
    if (region.species.some((e) => normName(e.scientific_name) === k)) {
      out.push(region.key);
    }
  }
  return out;
}

// Score a region against a user's lifelist. Returns which endemic
// species they've seen (matched by normalised scientific name so
// case/whitespace drift doesn't miss a hit) and which are still
// missing. Preserves the input order in the region so display is
// stable.
export interface EndemicScore {
  region: EndemicRegion;
  seen: EndemicSpecies[];
  missing: EndemicSpecies[];
  seenTaxonIds: Map<string, number>; // sci → taxon_id for tapping to open
}

export function scoreEndemics(
  region: EndemicRegion,
  lifelist: { taxon_id: number; scientific_name: string }[],
): EndemicScore {
  const seenKeys = new Map<string, number>();
  for (const e of lifelist) {
    const k = normName(e.scientific_name);
    if (k) seenKeys.set(k, e.taxon_id);
  }
  const seen: EndemicSpecies[] = [];
  const missing: EndemicSpecies[] = [];
  const seenTaxonIds = new Map<string, number>();
  for (const s of region.species) {
    const k = normName(s.scientific_name);
    const hit = seenKeys.get(k);
    if (hit != null) {
      seen.push(s);
      seenTaxonIds.set(s.scientific_name, hit);
    } else {
      missing.push(s);
    }
  }
  return { region, seen, missing, seenTaxonIds };
}
